// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React, { useState } from 'react';
import { useAuth } from '../AuthContext';

const DEMO_POSTS = [
  { id: 1, title: '10 Signs Your Business Needs a Website Redesign', status: 'Published', type: 'Blog Post', author: 'AI Writer', date: 'Feb 18, 2026', views: 1240, readTime: '6 min', category: 'Web Design', seoScore: 92 },
  { id: 2, title: 'The Complete Guide to Local SEO in 2026', status: 'Published', type: 'Pillar Page', author: 'AI Writer', date: 'Feb 12, 2026', views: 3400, readTime: '14 min', category: 'SEO', seoScore: 88 },
  { id: 3, title: 'How to Choose the Right CRM for Your Small Business', status: 'Draft', type: 'Blog Post', author: 'AI Writer', date: '', views: 0, readTime: '8 min', category: 'Business', seoScore: 75 },
  { id: 4, title: 'Email Marketing Best Practices That Actually Work', status: 'Scheduled', type: 'Blog Post', author: 'AI Writer', date: 'Feb 25, 2026', views: 0, readTime: '7 min', category: 'Marketing', seoScore: 85 },
  { id: 5, title: 'Why Every Business Needs a Google Business Profile', status: 'Published', type: 'How-To', author: 'AI Writer', date: 'Feb 5, 2026', views: 890, readTime: '5 min', category: 'Local SEO', seoScore: 90 },
];

const CATEGORIES = ['All', 'Web Design', 'SEO', 'Business', 'Marketing', 'Local SEO', 'Social Media', 'E-Commerce'];
const CONTENT_TYPES = ['Blog Post', 'Pillar Page', 'How-To', 'Case Study', 'Listicle', 'Video Script', 'Infographic', 'Newsletter'];

const S = {
  page: { padding: '24px', maxWidth: 1200, margin: '0 auto' },
  title: { fontSize: 28, fontWeight: 700, color: 'var(--text)', margin: 0 },
  subtitle: { fontSize: 14, color: '#999', marginTop: 4 },
  tabs: { display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' },
  tab: (active) => ({ padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14, background: active ? 'var(--accent, #d4a843)' : 'var(--card, #1a1a2e)', color: active ? '#000' : 'var(--text)' }),
  card: { background: 'var(--card, #1a1a2e)', borderRadius: 12, padding: 20, marginBottom: 16, border: '1px solid var(--border, #2a2a4a)' },
  cardTitle: { fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 12 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 },
  stat: { background: 'var(--card, #1a1a2e)', borderRadius: 12, padding: 20, textAlign: 'center', border: '1px solid var(--border, #2a2a4a)' },
  statValue: { fontSize: 28, fontWeight: 800, color: 'var(--accent, #d4a843)' },
  statLabel: { fontSize: 12, color: '#888', marginTop: 4 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid var(--border, #2a2a4a)', color: '#999', fontWeight: 600, fontSize: 11, textTransform: 'uppercase' },
  td: { padding: '10px 12px', borderBottom: '1px solid var(--border, #2a2a4a)', color: 'var(--text)' },
  badge: (color) => ({ display: 'inline-block', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: `${color}22`, color }),
  btn: (primary) => ({ padding: '10px 20px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14, background: primary ? 'var(--accent, #d4a843)' : 'var(--card, #1a1a2e)', color: primary ? '#000' : 'var(--text)', border: primary ? 'none' : '1px solid var(--border, #2a2a4a)' }),
  input: { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border, #2a2a4a)', background: 'var(--bg, #0f0f23)', color: 'var(--text)', fontSize: 14, boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border, #2a2a4a)', background: 'var(--bg, #0f0f23)', color: 'var(--text)', fontSize: 14, minHeight: 200, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' },
};

function ContentManagerPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('posts');
  const [filter, setFilter] = useState('All');
  const [creating, setCreating] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(false);
  const [topic, setTopic] = useState('');
  const [contentType, setContentType] = useState('Blog Post');
  const [keywords, setKeywords] = useState('');
  const [tone, setTone] = useState('Professional');
  const [wordCount, setWordCount] = useState('800-1500');
  const [editorContent, setEditorContent] = useState('');

  const totalViews = DEMO_POSTS.reduce((s, p) => s + p.views, 0);
  const published = DEMO_POSTS.filter(p => p.status === 'Published').length;
  const avgSeo = Math.round(DEMO_POSTS.reduce((s, p) => s + p.seoScore, 0) / DEMO_POSTS.length);
  const filtered = filter === 'All' ? DEMO_POSTS : DEMO_POSTS.filter(p => p.category === filter);

  const handleGenerate = () => {
    if (!topic) return;
    setGenerating(true);
    setTimeout(() => {
      setGenerating(false);
      setGenerated(true);
      setEditorContent(`# ${topic}\n\n## Introduction\nIn today's competitive digital landscape, understanding ${topic.toLowerCase()} is crucial for business success. This comprehensive guide covers everything you need to know.\n\n## Why This Matters\nBusinesses that master ${topic.toLowerCase()} see an average of 40% increase in engagement and 25% boost in conversions.\n\n## Key Strategies\n\n### 1. Research Your Audience\nBefore creating any content, understand who you're writing for. Use analytics data, customer surveys, and competitor analysis.\n\n### 2. Optimize for Search\nIncorporate your target keywords naturally: ${keywords || 'your target keywords'}. Use them in headings, first paragraph, and throughout the content.\n\n### 3. Create Value-First Content\nEvery piece of content should solve a problem or answer a question your audience has.\n\n### 4. Measure and Iterate\nTrack performance metrics and continuously improve based on data.\n\n## Conclusion\nMastering ${topic.toLowerCase()} takes time, but with the right strategy and consistent effort, the results are transformative.\n\n---\n*Generated by The Forge Standard AI Content Engine*`);
    }, 3000);
  };

  const TABS = [
    { id: 'posts', label: 'All Content', icon: '📄' },
    { id: 'create', label: 'AI Writer', icon: '🤖' },
    { id: 'calendar', label: 'Schedule', icon: '📅' },
    { id: 'media', label: 'Media Library', icon: '🖼️' },
    { id: 'analytics', label: 'Performance', icon: '📊' },
  ];

  return (
    <div style={S.page}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={S.title}>📝 Content Manager</h1>
          <p style={S.subtitle}>AI-powered content creation, blog management, and publishing</p>
        </div>
        <button style={S.btn(true)} onClick={() => { setTab('create'); setCreating(true); }}>+ Create Content</button>
      </div>

      <div style={S.tabs}>
        {TABS.map(t => (
          <button key={t.id} style={S.tab(tab === t.id)} onClick={() => setTab(t.id)}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      <div style={S.grid}>
        <div style={S.stat}><div style={S.statValue}>{DEMO_POSTS.length}</div><div style={S.statLabel}>Total Posts</div></div>
        <div style={S.stat}><div style={S.statValue}>{published}</div><div style={S.statLabel}>Published</div></div>
        <div style={S.stat}><div style={S.statValue}>{totalViews.toLocaleString()}</div><div style={S.statLabel}>Total Views</div></div>
        <div style={S.stat}><div style={S.statValue}>{avgSeo}%</div><div style={S.statLabel}>Avg SEO Score</div></div>
      </div>

      {/* All Content Tab */}
      {tab === 'posts' && (
        <div style={S.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={S.cardTitle}>Content Library</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {CATEGORIES.slice(0, 6).map(c => (
                <button key={c} style={{ ...S.tab(filter === c), fontSize: 11, padding: '4px 10px' }} onClick={() => setFilter(c)}>{c}</button>
              ))}
            </div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Title</th>
                  <th style={S.th}>Type</th>
                  <th style={S.th}>Status</th>
                  <th style={S.th}>Date</th>
                  <th style={S.th}>Views</th>
                  <th style={S.th}>SEO</th>
                  <th style={S.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(p => (
                  <tr key={p.id}>
                    <td style={{ ...S.td, fontWeight: 600, maxWidth: 300 }}>{p.title}</td>
                    <td style={S.td}><span style={S.badge('#2196f3')}>{p.type}</span></td>
                    <td style={S.td}><span style={S.badge(p.status === 'Published' ? '#4caf50' : p.status === 'Scheduled' ? '#ff9800' : '#888')}>{p.status}</span></td>
                    <td style={S.td}>{p.date || '—'}</td>
                    <td style={S.td}>{p.views.toLocaleString()}</td>
                    <td style={S.td}><span style={{ fontWeight: 700, color: p.seoScore >= 85 ? '#4caf50' : p.seoScore >= 70 ? '#ff9800' : '#f44336' }}>{p.seoScore}%</span></td>
                    <td style={S.td}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button style={{ ...S.btn(false), padding: '2px 8px', fontSize: 11 }}>Edit</button>
                        <button style={{ ...S.btn(false), padding: '2px 8px', fontSize: 11 }}>👁️</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* AI Writer Tab */}
      {tab === 'create' && (
        <div>
          <div style={S.card}>
            <div style={S.cardTitle}>AI Content Generator</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Topic / Title *</label>
                <input style={S.input} placeholder="e.g., How to Grow Your Business with Email Marketing" value={topic} onChange={e => setTopic(e.target.value)} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Content Type</label>
                <select style={S.input} value={contentType} onChange={e => setContentType(e.target.value)}>
                  {CONTENT_TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Target Keywords (comma-separated)</label>
                <input style={S.input} placeholder="email marketing, business growth, lead generation" value={keywords} onChange={e => setKeywords(e.target.value)} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Tone</label>
                <select style={S.input} value={tone} onChange={e => setTone(e.target.value)}>
                  <option>Professional</option>
                  <option>Casual</option>
                  <option>Authoritative</option>
                  <option>Friendly</option>
                  <option>Technical</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Word Count</label>
                <select style={S.input} value={wordCount} onChange={e => setWordCount(e.target.value)}>
                  <option value="300-500">Short (300-500)</option>
                  <option value="500-800">Medium (500-800)</option>
                  <option value="800-1500">Long (800-1500)</option>
                  <option value="1500-2500">Comprehensive (1500-2500)</option>
                  <option value="2500+">Pillar Content (2500+)</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Category</label>
                <select style={S.input}>
                  {CATEGORIES.filter(c => c !== 'All').map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <button style={S.btn(true)} onClick={handleGenerate} disabled={generating}>
              {generating ? '🤖 Generating content...' : '🤖 Generate with AI'}
            </button>
          </div>

          {generating && (
            <div style={{ ...S.card, textAlign: 'center', padding: 40 }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🤖</div>
              <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>AI is writing your content...</div>
              <div style={{ fontSize: 13, color: '#888' }}>Researching topic, optimizing for SEO, structuring content</div>
              <div style={{ width: 200, height: 4, background: '#1a1a2e', borderRadius: 2, margin: '16px auto', overflow: 'hidden' }}>
                <div style={{ width: '70%', height: '100%', background: 'var(--accent, #d4a843)', borderRadius: 2, animation: 'pulse 1.5s infinite' }} />
              </div>
            </div>
          )}

          {generated && (
            <div style={S.card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div style={S.cardTitle}>Content Editor</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button style={S.btn(true)}>📢 Publish</button>
                  <button style={S.btn(false)}>📅 Schedule</button>
                  <button style={S.btn(false)}>💾 Save Draft</button>
                  <button style={S.btn(false)}>🤖 Regenerate</button>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                {['B', 'I', 'U', 'H1', 'H2', 'H3', '📋', '🔗', '🖼️', '📹'].map(b => (
                  <button key={b} style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid var(--border, #2a2a4a)', background: 'transparent', color: 'var(--text)', cursor: 'pointer', fontWeight: b === 'B' ? 700 : 400, fontStyle: b === 'I' ? 'italic' : 'normal', textDecoration: b === 'U' ? 'underline' : 'none', fontSize: 13 }}>{b}</button>
                ))}
              </div>
              <textarea style={S.textarea} value={editorContent} onChange={e => setEditorContent(e.target.value)} />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 12, color: '#888' }}>
                <span>Words: {editorContent.split(/\s+/).filter(Boolean).length}</span>
                <span>Characters: {editorContent.length}</span>
                <span>Est. Read Time: {Math.max(1, Math.round(editorContent.split(/\s+/).filter(Boolean).length / 200))} min</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Schedule Tab */}
      {tab === 'calendar' && (
        <div style={S.card}>
          <div style={S.cardTitle}>Publishing Schedule</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 16 }}>
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
              <div key={d} style={{ textAlign: 'center', fontSize: 11, color: '#888', fontWeight: 600, padding: 8 }}>{d}</div>
            ))}
            {Array.from({ length: 28 }, (_, i) => (
              <div key={i} style={{ padding: 8, borderRadius: 6, border: '1px solid var(--border, #2a2a4a)', minHeight: 70, fontSize: 11, color: 'var(--text)' }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>{i + 1}</div>
                {i === 4 && <div style={{ padding: '2px 6px', borderRadius: 4, background: 'rgba(76, 175, 80, 0.15)', color: '#4caf50', fontSize: 9, marginBottom: 2 }}>Blog Post</div>}
                {i === 11 && <div style={{ padding: '2px 6px', borderRadius: 4, background: 'rgba(255, 152, 0, 0.15)', color: '#ff9800', fontSize: 9, marginBottom: 2 }}>Newsletter</div>}
                {i === 18 && <div style={{ padding: '2px 6px', borderRadius: 4, background: 'rgba(33, 150, 243, 0.15)', color: '#2196f3', fontSize: 9, marginBottom: 2 }}>Case Study</div>}
                {i === 24 && <div style={{ padding: '2px 6px', borderRadius: 4, background: 'rgba(156, 39, 176, 0.15)', color: '#9c27b0', fontSize: 9, marginBottom: 2 }}>How-To</div>}
              </div>
            ))}
          </div>
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>Upcoming</div>
            {DEMO_POSTS.filter(p => p.status === 'Scheduled').map(p => (
              <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border, #2a2a4a)', marginBottom: 6 }}>
                <div>
                  <span style={{ fontWeight: 600, color: 'var(--text)' }}>{p.title}</span>
                  <span style={{ fontSize: 11, color: '#888', marginLeft: 8 }}>{p.date}</span>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button style={{ ...S.btn(false), padding: '2px 8px', fontSize: 11 }}>Edit</button>
                  <button style={{ ...S.btn(false), padding: '2px 8px', fontSize: 11 }}>Reschedule</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Media Library Tab */}
      {tab === 'media' && (
        <div style={S.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={S.cardTitle}>Media Library</div>
            <button style={S.btn(true)}>📤 Upload</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 }}>
            {['hero-image.jpg', 'team-photo.jpg', 'product-1.png', 'logo-dark.svg', 'office.jpg', 'infographic.png', 'banner-ad.jpg', 'favicon.ico'].map((file, i) => (
              <div key={i} style={{ borderRadius: 8, border: '1px solid var(--border, #2a2a4a)', overflow: 'hidden', cursor: 'pointer' }}>
                <div style={{ height: 100, background: `hsl(${i * 40}, 30%, 20%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28 }}>
                  {file.endsWith('.svg') ? '🎨' : file.endsWith('.ico') ? '⭐' : '🖼️'}
                </div>
                <div style={{ padding: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{file}</div>
                  <div style={{ fontSize: 10, color: '#888' }}>{Math.round(50 + i * 30)} KB</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Performance Tab */}
      {tab === 'analytics' && (
        <div>
          <div style={S.card}>
            <div style={S.cardTitle}>Content Performance</div>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Content</th>
                  <th style={S.th}>Views</th>
                  <th style={S.th}>Avg. Time</th>
                  <th style={S.th}>Bounce Rate</th>
                  <th style={S.th}>Shares</th>
                  <th style={S.th}>Conversions</th>
                </tr>
              </thead>
              <tbody>
                {DEMO_POSTS.filter(p => p.status === 'Published').map(p => (
                  <tr key={p.id}>
                    <td style={{ ...S.td, fontWeight: 600, maxWidth: 280 }}>{p.title}</td>
                    <td style={S.td}>{p.views.toLocaleString()}</td>
                    <td style={S.td}>{p.readTime}</td>
                    <td style={S.td}>{Math.round(30 + Math.random() * 30)}%</td>
                    <td style={S.td}>{Math.round(p.views * 0.03)}</td>
                    <td style={S.td}>{Math.round(p.views * 0.015)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={S.card}>
            <div style={S.cardTitle}>Top Performing Topics</div>
            {['SEO', 'Web Design', 'Local SEO', 'Marketing'].map((topic, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: i < 3 ? '1px solid var(--border, #2a2a4a)' : 'none' }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', width: 120 }}>{topic}</span>
                <div style={{ flex: 1, height: 8, background: '#1a1a2e', borderRadius: 4, overflow: 'hidden', position: 'relative' }}>
                  <div style={{ position: 'absolute', top: 0, left: 0, height: '100%', width: `${90 - i * 15}%`, background: 'var(--accent, #d4a843)', borderRadius: 4 }} />
                </div>
                <span style={{ fontSize: 12, color: '#888', width: 60, textAlign: 'right' }}>{(4200 - i * 900).toLocaleString()} views</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default ContentManagerPage;
